import { Form } from 'antd';
import {
  Circle,
  MapContainer,
  Marker,
  TileLayer,
  useMap,
  useMapEvents,
} from 'react-leaflet';

import L from 'leaflet';
import { GeoSearchControl, OpenStreetMapProvider } from 'leaflet-geosearch';
import { useCallback, useEffect, useState } from 'react';

// Add these CSS imports
import 'leaflet-geosearch/dist/geosearch.css';
import 'leaflet/dist/leaflet.css';

const markerIcon = new L.Icon({
  iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
  iconSize: [25, 41],
  iconAnchor: [12, 41],
});

const defaultPosition: [number, number] = [23.7465, 90.376];

// Move SearchControl outside and pass updateLocation as prop
function SearchControl({
  onLocationUpdate,
}: {
  onLocationUpdate: (lat: number, lng: number) => void;
}) {
  const map = useMap();

  useEffect(() => {
    const provider = new OpenStreetMapProvider();
    const searchControl = new (GeoSearchControl as any)({
      provider,
      showMarker: false,
      autoClose: true,
      style: 'bar',
    });

    map.addControl(searchControl);

    const handleShowLocation = (e: any) => {
      onLocationUpdate(e.location.y, e.location.x);
      map.setView([e.location.y, e.location.x], 15);
    };

    map.on('geosearch/showlocation', handleShowLocation);

    return () => {
      map.off('geosearch/showlocation', handleShowLocation);
      map.removeControl(searchControl);
    };
  }, [map, onLocationUpdate]);

  return null;
}

// New component to handle map clicks
function MapClickHandler({
  onLocationUpdate,
}: {
  onLocationUpdate: (lat: number, lng: number) => void;
}) {
  useMapEvents({
    click: (e) => {
      onLocationUpdate(e.latlng.lat, e.latlng.lng);
    },
  });

  return null;
}

// Component to handle map center updates
function MapCenterUpdater({ position }: { position: [number, number] }) {
  const map = useMap();

  useEffect(() => {
    map.setView(position, map.getZoom());
  }, [position, map]);

  return null;
}

export default function GeoFenceMap({
  onMapChange,
  radius,
  defaultValues,
  viewOnly = false,
}: {
  onMapChange: (lat: string, long: string) => void;
  radius?: number;
  viewOnly?: boolean;
  defaultValues?: { lat?: number; long?: number };
}) {
  const [form] = Form.useForm();
  const [position, setPosition] = useState<[number, number]>(defaultPosition);

  // Use useCallback to stabilize the function reference
  const updateLocation = useCallback(
    (lat: number, lng: number) => {
      if (viewOnly) return;
      setPosition([lat, lng]);
      if (onMapChange) {
        onMapChange(lat.toFixed(6), lng.toFixed(6));
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [form],
  );
  
  useEffect(() => {
    const lat = defaultValues?.lat;
    const long = defaultValues?.long;

    if (
      lat !== null &&
      long !== null &&
      lat !== undefined &&
      long !== undefined &&
      !Number.isNaN(Number(lat)) &&
      !Number.isNaN(Number(long))
    ) {
      setPosition([Number(lat), Number(long)]);
    }
  }, [defaultValues]);

  return (
    <>
      <div className="rounded-lg overflow-hidden mb-6">
        <MapContainer center={position} zoom={14} className="h-[280px] w-full">
          <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />

          {/* Update map center when position changes */}
          <MapCenterUpdater position={position} />

          {!viewOnly && <SearchControl onLocationUpdate={updateLocation} />}

          {/* Add the click handler */}
          {!viewOnly && <MapClickHandler onLocationUpdate={updateLocation} />}

          <Marker
            draggable={!viewOnly}
            position={position}
            icon={markerIcon}
            eventHandlers={
              viewOnly
                ? undefined
                : {
                    dragend: (e) => {
                      const latlng = e.target.getLatLng();
                      updateLocation(latlng.lat, latlng.lng);
                    },
                  }
            }
          />

          {radius && radius > 0 && (
            <Circle
              center={position}
              radius={radius}
              pathOptions={{ color: '#0bb449' }}
            />
          )}
        </MapContainer>
      </div>
    </>
  );
}
